import {call, put, select, takeEvery, take} from 'redux-saga/effects';
import {pushDataLayer} from '@optimaxdev/analytics/mobile';

import {setHtoMode, setHtoEnabled} from 'reducers/product/productHto/productHto';
import {getIsHtoEnabledForPdp} from 'selectors/product/hto/hto';
import {getActiveChildrenProductId} from 'selectors/product/product';
import {sessionStore} from 'libs/storage/storage';

import {PRODUCT} from 'constants/actions';
import {
    HTO_PDP_MODE,
    HTO_SESSION_STORAGE_TITLE,
    HTO_PDP_URL_PARAMETER,
} from 'constants/homeTryOn/homeTryOn';
import {isHtoEnabled} from 'constants/flags/flags';
import {isConfigurePage} from 'selectors/routerInfo/routerInfo';

/**
 * Send analytics depending on pdp mode
 */
export function* setDataLayer() {
    const isExperimentEnabled = yield call(isHtoEnabled);
    if (!isExperimentEnabled) return;
    const isHtoEnabledForPDP = yield select(getIsHtoEnabledForPdp);
    yield call(pushDataLayer, {
        event: 'GAEventGenericTriggerInteraction',
        eventCategory: 'HTO',
        eventAction: 'PDP - State',
        eventLabel: isHtoEnabledForPDP ? 'On' : 'Off',
    });
}

/**
 * Return is HTO mode enabled from URL
 * @returns {boolean} - should enable HTO mode
 */
export const isEnabledFromUrl = (): boolean =>
    Boolean(window.location.search?.includes(HTO_PDP_URL_PARAMETER));

/**
 * Checks URL and starts HTO session for PDP if the URL contains special HTO parameter
 */
export function* checkHtoUrlParameter() {
    const isForceEnabled = yield call(isEnabledFromUrl);
    yield call([sessionStore, 'set'], HTO_SESSION_STORAGE_TITLE, Number(isForceEnabled));
}

/**
 * Setup hto PDP state for eligible product
 */
export function* setStateForEligibleProduct() {
    yield call(checkHtoUrlParameter);

    const isHtoSession = yield call([sessionStore, 'get'], HTO_SESSION_STORAGE_TITLE);

    const mode = isHtoSession ? HTO_PDP_MODE.PREFER_HTO : HTO_PDP_MODE.PREFER_BUY_NOW;
    yield put(setHtoMode(isHtoSession ? HTO_PDP_MODE.PREFER_HTO : HTO_PDP_MODE.PREFER_BUY_NOW));

    const isConfigure = yield select(isConfigurePage);
    if (!isConfigure) {
        const isEnabledUrlParam = yield call(isEnabledFromUrl);
        yield put(setHtoEnabled(isEnabledUrlParam || mode === HTO_PDP_MODE.PREFER_HTO));
    }
}

/**
 * Check is product eligible for HTO and run state setup
 */
export function* setupHtoForProductPage() {
    const isExperimentEnabled = yield call(isHtoEnabled);
    if (!isExperimentEnabled) return;

    const hasActiveProduct = yield select(getActiveChildrenProductId);

    if (!hasActiveProduct) {
        yield take(PRODUCT.SET_ACTIVE_PRODUCT_ID);
    }

    yield call(setStateForEligibleProduct);
}

/**
 * Makes HTO mode not preferred for PDP
 */
export const stopHtoSession = () => {
    sessionStore.remove(HTO_SESSION_STORAGE_TITLE);
};

/**
 * Root saga
 */
export default function* homeTryOnProductSaga() {
    yield takeEvery(PRODUCT.OPENED, setupHtoForProductPage);
    yield takeEvery(PRODUCT.CLEAR_PRODUCT, stopHtoSession);
    yield takeEvery(setHtoEnabled, setDataLayer);
}
